import * as React from 'react';
// @ts-expect-error
import type {RouterHistory} from 'react-router-dom';

import type {ColorName} from '../../colors/colors';
import {CueCard} from '../../cue-card/cue-card';
import type {CueCardCta, GradientConfig} from '../../cue-card/cue-card';
import {Illustration} from '../../illustrations/illustration';

type Props = {
    title: string;
    description: string;
    history: RouterHistory;
    exploreLink?: string;
    thumbnail?: {
        backgroundColor?: ColorName;
        backgroundColorHover?: ColorName;
        backgroundGradientHover?: GradientConfig;
        illustration: string | React.ReactNode;
    };
    icon?: React.ComponentType<any>;
    variant: 'ai' | 'feature';
    cta?: CueCardCta;
};

/**
 * A small wrapper around the `CueCard` component for reusablility in
 * other usages that share the same styling and base component.
 */
export function FeatureCard(props: Props) {
    const defaultCta = React.useMemo(() => {
        // If a cta is already provided, don't create a default cta
        if (props.cta) {
            return undefined;
        }

        // If explore link is provided, create a default cta
        if (props.exploreLink) {
            return {
                text: props.thumbnail ? 'Explore' : '',
                actionType: 'routed-link',
                url: props.exploreLink,
                newTab: false,
            };
        }

        return undefined;
    }, [props.cta, props.exploreLink, props.thumbnail]);

    return (
        <CueCard
            title={props.title}
            description={props.description}
            history={props.history}
            thumbnailBackgroundColor={props.thumbnail && props.thumbnail.backgroundColor}
            thumbnailBackgroundColorHover={props.thumbnail && props.thumbnail.backgroundColorHover}
            thumbnailBackgroundGradientHover={
                props.thumbnail && props.thumbnail.backgroundGradientHover
            }
            thumbnailIllustration={
                props.thumbnail && props.thumbnail.illustration ? (
                    typeof props.thumbnail.illustration === 'string' ? (
                        <Illustration src={props.thumbnail.illustration} />
                    ) : (
                        props.thumbnail.illustration
                    )
                ) : undefined
            }
            thumbnailPosition='full'
            // @ts-expect-error
            cta={props.cta || defaultCta}
            icon={props.icon}
            variant={props.variant}
            fitHeight={false}
        />
    );
}
